<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { FirebaseError } from 'firebase/app';
import { useAuthStore, routeForBackendAuthState } from '@/stores/auth';
import { requestEmailVerification } from '@/api';
import { recaptchaHeaders } from '@/utils/recaptcha';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaGoogleButton from '@/components/design/domain/auth/GaiaGoogleButton.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';

const authStore = useAuthStore();
const router = useRouter();
const email = ref<string>('');
const password = ref<string>('');
const error = ref<string | null>(null);
const info = ref<string | null>(null);
const loading = ref<boolean>(false);
const sendingReset = ref<boolean>(false);
// Post-migration, an "existing" account may stem from the legacy invite flow,
// with a password the user never knew — offer a reset alongside login.
const showExistingAccountActions = ref<boolean>(false);

onMounted((): void => {
  // Resumability: a returning half-signed-up visitor continues where they
  // left off instead of re-entering credentials.
  if (authStore.user && !authStore.isAuthenticated) {
    const partialRoute = routeForBackendAuthState(authStore.backendAuthState, '');
    if (partialRoute) {
      void router.replace(partialRoute);
    }
  }
});

async function handleEmailSignUp(): Promise<void> {
  if (!email.value) {
    error.value = 'Please enter your email';
    return;
  }

  if (!password.value) {
    error.value = 'Please enter a password';
    return;
  }

  // Simple email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    error.value = 'Please enter a valid email address';
    return;
  }

  // Password validation (minimum 6 characters)
  if (password.value.length < 6) {
    error.value = 'Password must be at least 6 characters long';
    return;
  }

  loading.value = true;
  error.value = null;
  info.value = null;
  showExistingAccountActions.value = false;

  try {
    // Create user with email and password in Firebase
    await createUserWithEmailAndPassword(getFirebaseAuth(), email.value, password.value);

    // The backend sends the verification mail. Best-effort: without the
    // ?sent flag the verify screen offers to send one instead.
    let mailSent = false;
    try {
      const { response } = await requestEmailVerification({
        headers: await recaptchaHeaders('email_verification_request'),
        throwOnError: false,
      });
      mailSent = response?.ok ?? false;
    } catch (error_: unknown) {
      logger.error('Error requesting verification email:', error_);
    }

    void router.push(mailSent ? { path: '/verify-email', query: { sent: '1' } } : '/verify-email');
  } catch (error_: unknown) {
    logger.error('Email sign-up error:', error_);

    // Handle Firebase-specific errors
    if (error_ instanceof FirebaseError) {
      switch (error_.code) {
        case 'auth/email-already-in-use':
          error.value =
            'This email is already registered. Sign in instead, or reset your password.';
          showExistingAccountActions.value = true;
          break;
        case 'auth/invalid-email':
          error.value = 'Please enter a valid email address.';
          break;
        case 'auth/weak-password':
          error.value = 'Password should be at least 6 characters long.';
          break;
        default:
          error.value = 'Error during sign-up. Please try again.';
      }
    } else {
      error.value = 'Error during sign-up. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

async function handleSendPasswordReset(): Promise<void> {
  sendingReset.value = true;
  try {
    await sendPasswordResetEmail(getFirebaseAuth(), email.value);
    error.value = null;
    showExistingAccountActions.value = false;
    info.value = 'Password reset email sent. Please check your inbox.';
  } catch (error_: unknown) {
    logger.error('Password reset error:', error_);
    error.value = 'Could not send the password reset email. Please try again.';
  } finally {
    sendingReset.value = false;
  }
}

/**
 * Handle Google sign-up using Firebase authentication
 */
const handleGoogleSignUp = async (): Promise<void> => {
  try {
    loading.value = true;
    error.value = null;
    info.value = null;
    showExistingAccountActions.value = false;

    // Google identities are verified inherently — go straight to the profile.
    const provider = new GoogleAuthProvider();
    await signInWithPopup(getFirebaseAuth(), provider);

    void router.push('/sign-up/complete');
  } catch (error_: unknown) {
    logger.error('Google sign-up error:', error_);

    // Handle Firebase-specific errors
    if (error_ instanceof FirebaseError) {
      switch (error_.code) {
        case 'auth/popup-closed-by-user':
          error.value = 'Sign-up was cancelled. Please try again.';
          break;
        case 'auth/network-request-failed':
          error.value = 'Network error. Please check your internet connection.';
          break;
        case 'auth/too-many-requests':
          error.value = 'Too many attempts. Please try again later.';
          break;
        default:
          error.value = 'Error during Google sign-up. Please try again.';
      }
    } else {
      error.value = 'Error during Google sign-up. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <gaia-auth-card title="Create your account" subtitle="Get started with 500 free credits.">
    <n-flex vertical :size="16">
      <!-- The already-registered case is the one error with somewhere to go,
           so it carries its two ways out rather than just naming the problem. -->
      <gaia-banner v-if="error" plain type="error">
        {{ error }}
        <n-flex v-if="showExistingAccountActions" :size="14" :style="{ marginTop: '6px' }">
          <gaia-link to="/login">Go to login</gaia-link>
          <gaia-link :disabled="sendingReset" @click="handleSendPasswordReset">
            {{ sendingReset ? 'Sending…' : 'Send password reset email' }}
          </gaia-link>
        </n-flex>
      </gaia-banner>
      <gaia-banner v-if="info" plain type="success">{{ info }}</gaia-banner>

      <gaia-form-field label="Email" variant="uppercase">
        <gaia-input
          :value="email"
          size="large"
          placeholder="you@firm.com"
          :disabled="loading"
          :input-props="{ type: 'email', autocomplete: 'email' }"
          style="width: 100%"
          @update:value="(v: string) => (email = v.toLowerCase())"
          @keydown.enter="handleEmailSignUp"
        />
      </gaia-form-field>

      <gaia-form-field label="Password" variant="uppercase" hint="At least 6 characters">
        <gaia-password-input
          v-model:value="password"
          size="large"
          placeholder="••••••••"
          :disabled="loading"
          :input-props="{ autocomplete: 'new-password' }"
          style="width: 100%"
          @keydown.enter="handleEmailSignUp"
        />
      </gaia-form-field>
    </n-flex>

    <gaia-button
      type="primary"
      size="large"
      block
      :loading="loading"
      :style="{ marginTop: '22px' }"
      @click="handleEmailSignUp"
    >
      Continue with email
    </gaia-button>

    <gaia-divider variant="subtle">or</gaia-divider>
    <gaia-google-button :disabled="loading" @click="handleGoogleSignUp">
      Sign up with Google
    </gaia-google-button>

    <template #footer>
      By signing up you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>

  <n-flex align="center" justify="center" :size="6" :style="{ marginTop: '18px' }">
    <gaia-text size="dense" tone="tertiary">Already have an account?</gaia-text>
    <gaia-link to="/login">Log in</gaia-link>
  </n-flex>
</template>
